"use client"

// src/components/pages/RestaurantManagementPage.jsx
import { useState, useEffect } from "react"
import DashboardTemplate from "../templates/DashboardTemplate"
import Input from "../atoms/Input"
import Button from "../atoms/Button"
import api from "../../services/api"
import { useAuth } from "../../hooks/useAuth"
import FileUpload from "../atoms/FileUpload"

const Spinner = () => (
  <div className="flex justify-center items-center py-8">
    <svg
      className="animate-spin h-10 w-10 text-amber-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
    </svg>
  </div>
)

const RestaurantManagementPage = () => {
  const { user } = useAuth()
  const ownerId = user ? user._id || user.id : null

  const [restaurant, setRestaurant] = useState(null)
  const [form, setForm] = useState({
    name: "",
    address: "",
    description: "",
    phone: "",
    email: "",
    imageUrl: "",
    cuisineType: "",
    openingHours: "",
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [notification, setNotification] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        if (!ownerId) throw new Error("Owner ID is missing")
        const response = await api.get(`/restaurants/owner/${ownerId}`)
        if (response.data) {
          setRestaurant(response.data)
          setForm({
            name: response.data.name || "",
            address: response.data.address || "",
            description: response.data.description || "",
            phone: response.data.contactInfo?.phone || "",
            email: response.data.contactInfo?.email || "",
            imageUrl: response.data.imageUrl || "",
            cuisineType: response.data.cuisineType || "",
            openingHours: response.data.openingHours || "",
          })
          setIsEditing(true)
          setError(null)
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setRestaurant(null)
          setIsEditing(false)
          setError(null)
        } else {
          const errMsg = err.response?.data?.error || err.message
          setError(errMsg)
        }
      } finally {
        setLoading(false)
      }
    }

    if (user && ownerId) {
      fetchRestaurant()
    }
  }, [user, ownerId])

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleImageUpload = (url) => {
    setForm({ ...form, imageUrl: url })
  }

  const showNotification = (type, message) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 5000)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    // build payload to match Joi schema exactly
    const payload = {
      name: form.name,
      address: form.address,
      description: form.description,
      owner: ownerId,
      contactInfo: {
        phone: form.phone,
        email: form.email,
      },
      imageUrl: form.imageUrl,
      cuisineType: form.cuisineType,
      openingHours: form.openingHours,
    }

    try {
      if (isEditing && restaurant && restaurant._id) {
        const response = await api.put(`/restaurants/${restaurant._id}`, payload)
        setRestaurant(response.data)
        showNotification("success", "Restaurant details updated successfully")
      } else {
        const response = await api.post("/restaurants", payload)
        setRestaurant(response.data)
        setIsEditing(true)
        showNotification("success", "Restaurant created successfully")
      }
    } catch (err) {
      console.error("Error saving restaurant:", err)
      const errMsg = err.response?.data?.error || err.message
      showNotification("error", "Failed to save restaurant details: " + errMsg)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading)
    return (
      <DashboardTemplate>
        <div className="p-6 max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6 text-amber-800 border-b border-amber-200 pb-2">
            Restaurant Management
          </h1>
          <Spinner />
        </div>
      </DashboardTemplate>
    )

  if (error)
    return (
      <DashboardTemplate>
        <div className="p-6 max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-6 text-amber-800 border-b border-amber-200 pb-2">
            Restaurant Management
          </h1>
          <div className="bg-red-100 border border-red-400 text-red-700 p-4 rounded-lg">
            <p className="font-bold">Error</p>
            <p>{error}</p>
          </div>
        </div>
      </DashboardTemplate>
    )

  return (
    <DashboardTemplate>
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-6 text-amber-800 border-b border-amber-200 pb-2">
          {isEditing ? "Edit Restaurant Details" : "Create Your Restaurant"}
        </h1>

        {notification && (
          <div
            className={`mb-6 px-4 py-3 rounded-lg ${
              notification.type === "error"
                ? "bg-red-100 text-red-700 border border-red-300"
                : "bg-green-100 text-green-700 border border-green-300"
            }`}
          >
            {notification.message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-md border border-amber-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 font-medium text-amber-700">Restaurant Name</label>
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
                className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Enter restaurant name"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-amber-700">Cuisine Type</label>
              <Input
                name="cuisineType"
                value={form.cuisineType}
                onChange={handleChange}
                className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
                placeholder="e.g., Italian, Mexican, Indian"
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 font-medium text-amber-700">Address</label>
            <Input
              name="address"
              value={form.address}
              onChange={handleChange}
              required
              className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
              placeholder="Full restaurant address"
            />
          </div>

          <div>
            <label className="block mb-2 font-medium text-amber-700">Description</label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              rows="4"
              className="w-full p-3 border border-amber-300 rounded-md focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Tell customers about your restaurant"
            ></textarea>
          </div>

          <div>
            <label className="block mb-2 font-medium text-amber-700">Opening Hours</label>
            <Input
              name="openingHours"
              value={form.openingHours}
              onChange={handleChange}
              className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
              placeholder="e.g., Mon-Fri: 9AM-10PM, Sat-Sun: 10AM-11PM"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2 font-medium text-amber-700">Phone</label>
              <Input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                required
                className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Contact phone number"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-amber-700">Email</label>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                className="border-amber-300 focus:ring-amber-500 focus:border-amber-500"
                placeholder="Contact email address"
              />
            </div>
          </div>

          <div>
            <label className="block mb-2 font-medium text-amber-700">Restaurant Image</label>
            <FileUpload onUpload={handleImageUpload} />

            {form.imageUrl ? (
              <div className="mt-4 relative">
                <img
                  src={form.imageUrl || "/placeholder.svg"}
                  alt={form.name}
                  className="w-full h-48 object-cover rounded-md"
                />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, imageUrl: "" })}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  aria-label="Remove image"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="mt-4 h-48 w-full flex items-center justify-center bg-gray-100 text-gray-500 rounded-md border border-dashed border-gray-300">
                <div className="text-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="mt-2">No image uploaded</p>
                </div>
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="w-full py-3 text-lg bg-amber-600 hover:bg-amber-700 text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {isEditing ? "Updating..." : "Creating..."}
              </span>
            ) : isEditing ? (
              "Update Restaurant Details"
            ) : (
              "Create Restaurant"
            )}
          </Button>
        </form>
      </div>
    </DashboardTemplate>
  )
}

export default RestaurantManagementPage
